import React from 'react';
import Box from '@mui/material/Box';
import Grid2 from '@mui/material/Grid2';
import CircularProgress from '@mui/material/CircularProgress';
import InputAdornment from '@mui/material/InputAdornment';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useFormikContext } from 'formik';
import { useTheme } from '@mui/material/styles';
import { CheckCircle, WarningAmber } from '@mui/icons-material';
import { UpdateAccountInput, User, Button, TextField } from '@elevator/ui';
import { useVerifyEmailModal } from '../../organisms/VerifyEmailModal/VerifyEmailModalContext';
import SectionHeader from '../SectionHeader/SectionHeader';
import useStyles from './styles';

export interface AccountSettingsFormProps {
  /** Current user (for emailVerified and initial display) */
  currentUser: User;
  /** Called when a field blurs; parent can track touched fields for partial updates */
  onBlur?: (field: string, value: string) => void;
  /** Called when "Change Password" is clicked; parent opens the password modal */
  onChangePasswordClick: () => void;
  /** Save button label. Default "Save" */
  submitLabel?: string;
  /** Whether the submit button is disabled (e.g. !isValid || isSubmitting) */
  submitDisabled?: boolean;
}

/**
 * AccountSettingsForm molecule: form fields for account settings (first name, last name,
 * email, username) plus email verification status, Change Password action, and Save button.
 * Designed to be used on the account settings page; parent must wrap in Formik with
 * UpdateAccountInput and provide initialValues, validationSchema, onSubmit.
 */
const AccountSettingsForm = ({
  currentUser,
  onBlur,
  onChangePasswordClick,
  submitLabel = 'Save',
  submitDisabled = false,
}: AccountSettingsFormProps) => {
  const { classes } = useStyles();
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down('md'));
  const { openModal: openVerifyModal } = useVerifyEmailModal();

  const {
    values,
    touched,
    errors,
    handleChange,
    setFieldTouched,
    submitForm,
    isSubmitting,
  } = useFormikContext<UpdateAccountInput>();

  const handleInputChange = (
    fieldName: string,
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const value = event.target.value;
    handleChange(event);
    setFieldTouched(fieldName, true);
    onBlur?.(fieldName, value);
  };

  return (
    <form
      className={classes.root}
      data-component="AccountSettingsForm"
      onSubmit={(e) => e.preventDefault()}
    >
      <Grid2 container direction="column" spacing={2}>
        <Grid2 size={12} className={classes.textFieldContainer}>
          <Grid2 container direction={mobile ? 'column' : 'row'} spacing={2}>
            <Grid2 size={{ xs: 12, md: 6 }} className={classes.textFieldContainer}>
              <TextField
                id="firstName"
                label="First Name"
                fullWidth
                required
                value={values.firstName}
                onChange={(e) => handleInputChange('firstName', e)}
                error={touched.firstName && Boolean(errors.firstName)}
                helperText={touched.firstName ? errors.firstName : ''}
                disabled={isSubmitting}
              />
            </Grid2>
            <Grid2 size={{ xs: 12, md: 6 }} className={classes.textFieldContainer}>
              <TextField
                id="lastName"
                label="Last Name"
                fullWidth
                required
                value={values.lastName}
                onChange={(e) => handleInputChange('lastName', e)}
                error={touched.lastName && Boolean(errors.lastName)}
                helperText={touched.lastName ? errors.lastName : ''}
                disabled={isSubmitting}
              />
            </Grid2>
          </Grid2>
        </Grid2>

        <Grid2 size={12} className={classes.emailFieldContainer}>
          <TextField
            id="email"
            label="Email"
            fullWidth
            required
            value={values.email}
            onChange={(e) => handleInputChange('email', e)}
            error={touched.email && Boolean(errors.email)}
            helperText={touched.email ? errors.email : ''}
            disabled={isSubmitting}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    {currentUser.emailVerified ? (
                      <CheckCircle
                        className={classes.emailVerifiedIcon}
                        color="success"
                        fontSize="small"
                      />
                    ) : (
                      <WarningAmber
                        className={classes.emailWarningIcon}
                        color="warning"
                        fontSize="small"
                      />
                    )}
                  </InputAdornment>
                ),
              },
            }}
          />
          {currentUser.emailVerified ? (
            <Typography variant="caption" className={classes.verifiedLabel}>
              Verified
            </Typography>
          ) : (
            <Button
              variant="outlined"
              size="small"
              className={classes.verifyEmailButton}
              onClick={openVerifyModal}
            >
              Verify Email
            </Button>
          )}
        </Grid2>

        <Grid2 size={12} className={classes.textFieldContainer}>
          <TextField
            id="preferredUsername"
            label="Username"
            fullWidth
            required
            value={values.preferredUsername}
            onChange={(e) => handleInputChange('preferredUsername', e)}
            error={touched.preferredUsername && Boolean(errors.preferredUsername)}
            helperText={
              touched.preferredUsername
                ? errors.preferredUsername
                : 'Your username is used to log in to your account and considered your elevator handle'
            }
            disabled={isSubmitting}
          />
        </Grid2>

        <Grid2 size={12}>
          <Button variant="text" onClick={onChangePasswordClick} disabled={isSubmitting}>
            Change Password
          </Button>
        </Grid2>
      </Grid2>

      <Box className={classes.saveButtonContainer}>
        <Button
          type="button"
          variant="contained"
          size="large"
          disabled={submitDisabled}
          onClick={() => submitForm()}
        >
          {isSubmitting && (
            <Box component="span" sx={{ display: 'inline-flex', mr: 1 }}>
              <CircularProgress color="inherit" size={18} />
            </Box>
          )}
          {isSubmitting ? 'Saving Changes' : submitLabel}
        </Button>
      </Box>
    </form>
  );
};

export default AccountSettingsForm;
